import React, { useState } from 'react';
import { Grid3x3, X, TrendingUp, Users, FileText, CheckCircle2, Clock, Building2, Settings, Eye, EyeOff } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';


const CustomWidgetsPanel = ({ isOpen, onClose }) => {
  const [availableWidgets] = useState([
    {
      id: 'conversion-trend',
      name: 'Conversion-Trend',
      type: 'line',
      icon: 'TrendingUp',
      description: 'Zeigt die Conversion-Rate über Zeit',
      size: 'medium'
    },
    {
      id: 'status-distribution',
      name: 'Status-Verteilung',
      type: 'pie',
      icon: 'FileText',
      description: 'Antragsverteilung nach Status',
      size: 'small'
    },
    {
      id: 'dealership-performance',
      name: 'Dealership-Leistung',
      type: 'bar',
      icon: 'Building2',
      description: 'Top-performing Autohäuser',
      size: 'large'
    },
    {
      id: 'hourly-applications',
      name: 'Stündliche Anträge',
      type: 'bar',
      icon: 'Clock',
      description: 'Anträge nach Tageszeit',
      size: 'medium'
    },
    {
      id: 'user-activity',
      name: 'Benutzeraktivität',
      type: 'line',
      icon: 'Users',
      description: 'Aktive Benutzer im Zeitverlauf',
      size: 'medium'
    },
    {
      id: 'approval-rate',
      name: 'Genehmigungsrate',
      type: 'stat',
      icon: 'CheckCircle2',
      description: 'Aktuelle Genehmigungsrate',
      size: 'small'
    }
  ]);

  const [activeWidgets, setActiveWidgets] = useState([
    'conversion-trend',
    'status-distribution',
    'dealership-performance'
  ]);

  const conversionData = [
    { month: 'Jul', rate: 82 },
    { month: 'Aug', rate: 85 },
    { month: 'Sep', rate: 88 },
    { month: 'Okt', rate: 86 },
    { month: 'Nov', rate: 87 },
    { month: 'Dez', rate: 89 }
  ];

  const statusData = [
    { name: 'Genehmigt', value: 548, color: '#10b981' },
    { name: 'In Bearbeitung', value: 287, color: '#f59e0b' },
    { name: 'Abgelehnt', value: 142, color: '#ef4444' },
    { name: 'Offen', value: 270, color: '#3b82f6' }
  ];

  const dealershipData = [
    { name: 'München', applications: 187 },
    { name: 'Berlin', applications: 156 },
    { name: 'Hamburg', applications: 142 },
    { name: 'Köln', applications: 128 },
    { name: 'Frankfurt', applications: 115 }
  ];

  const hourlyData = [
    { hour: '6h', count: 12 },
    { hour: '9h', count: 45 },
    { hour: '12h', count: 78 },
    { hour: '15h', count: 62 },
    { hour: '18h', count: 34 },
    { hour: '21h', count: 18 }
  ];

  const toggleWidget = (widgetId) => {
    if (activeWidgets?.includes(widgetId)) {
      setActiveWidgets(activeWidgets?.filter(id => id !== widgetId));
    } else {
      setActiveWidgets([...activeWidgets, widgetId]);
    }
  };

  const renderWidget = (widget) => {
    const isActive = activeWidgets?.includes(widget?.id);

    switch(widget?.type) {
      case 'line':
        if (widget?.id === 'conversion-trend') {
          return (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={conversionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Line type="monotone" dataKey="rate" stroke="#3b82f6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          );
        }
        break;

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                outerRadius={70}
                fill="#8884d8"
                dataKey="value"
                label
              >
                {statusData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'bar':
        const data = widget?.id === 'dealership-performance' ? dealershipData : hourlyData;
        const dataKey = widget?.id === 'dealership-performance' ? 'applications' : 'count';
        
        return (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey={widget?.id === 'dealership-performance' ? 'name' : 'hour'} stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip />
              <Bar dataKey={dataKey} fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'stat':
        return (
          <div className="flex flex-col items-center justify-center h-[200px]">
            <div className="text-5xl font-bold text-success mb-2">87.3%</div>
            <div className="text-sm text-muted-foreground">Durchschnittlich</div>
            <div className="text-xs text-success mt-2">+3.2% vs. Vormonat</div>
          </div>
        );

      default:
        return null;
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      TrendingUp,
      FileText,
      Building2,
      Clock,
      Users,
      CheckCircle2
    };
    const Icon = icons?.[iconName];
    return Icon ? <Icon className="h-5 w-5" /> : null;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Grid3x3 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-bold text-foreground">
                Dashboard Widgets
              </h2>
              <p className="text-sm text-muted-foreground">
                Widgets hinzufügen oder entfernen
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-160px)] p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Settings className="h-5 w-5 text-primary" />
                Verfügbare Widgets
              </h3>

              <div className="space-y-3">
                {availableWidgets?.map((widget) => {
                  const isActive = activeWidgets?.includes(widget?.id);
                  return (
                    <div
                      key={widget?.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-all ${
                        isActive 
                          ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => toggleWidget(widget?.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`p-2 rounded-lg ${isActive ? 'bg-primary text-white' : 'bg-muted text-foreground'}`}>
                            {getIcon(widget?.icon)}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-foreground mb-1">
                              {widget?.name}
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              {widget?.description}
                            </p>
                            <div className="mt-2">
                              <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                                {widget?.size === 'small' ? 'Klein' : widget?.size === 'medium' ? 'Mittel' : 'Groß'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <button className="ml-3">
                          {isActive ? (
                            <Eye className="h-5 w-5 text-success" />
                          ) : (
                            <EyeOff className="h-5 w-5 text-muted-foreground" />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">
                Vorschau ({activeWidgets?.length} aktiv)
              </h3>

              <div className="space-y-4">
                {availableWidgets
                  ?.filter(w => activeWidgets?.includes(w?.id))
                  ?.map((widget) => (
                    <div
                      key={widget?.id}
                      className="bg-white border border-border rounded-lg p-4 shadow-sm"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-primary/10 rounded">
                            {getIcon(widget?.icon)}
                          </div>
                          <h4 className="font-semibold text-foreground">{widget?.name}</h4>
                        </div>
                        <button
                          onClick={() => toggleWidget(widget?.id)}
                          className="p-1.5 hover:bg-destructive/10 rounded transition-colors"
                        >
                          <X className="h-4 w-4 text-destructive" />
                        </button>
                      </div>
                      {renderWidget(widget)}
                    </div>
                  ))}

                {activeWidgets?.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Grid3x3 className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium mb-2">Keine Widgets ausgewählt</p>
                    <p className="text-sm">Wähle Widgets aus der Liste aus, um sie hier zu sehen</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-border bg-muted/30">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              {activeWidgets?.length} von {availableWidgets?.length} Widgets aktiv
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose} iconName="">
                Abbrechen
              </Button>
              <Button onClick={onClose} iconName="">
                Änderungen speichern
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomWidgetsPanel;